import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Users, CreditCard, ShieldCheck, Sun, Landmark, Share2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import './WeekendTour.css';

const WA_NUMBER = '917827327243';
const WA_TEXT = 'Hi! I want to book the 2-Day Delhi + Agra Weekend (₹7,499 per person). Please share the next departure dates and seats.';
export const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_TEXT)}`;

const unsplash = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=900&q=80`;
const galleryImages = [
  { src: unsplash('1705524220939-dac17cf94236'), alt: 'Red Fort, Delhi' },
  { src: unsplash('1632426237957-5ea14aae7100'), alt: 'Qutub Minar, Delhi' },
  { src: unsplash('1646667562661-8f2a020e16ef'), alt: "Humayun's Tomb, Delhi" },
  { src: 'https://images.pexels.com/photos/14907353/pexels-photo-14907353.jpeg?auto=compress&cs=tinysrgb&w=900', alt: 'Akshardham Temple, Delhi' },
  { src: unsplash('1587135941948-670b381f08ce'), alt: 'Taj Mahal, Agra' },
  { src: unsplash('1732639840730-a3cdf066a9e6'), alt: 'Agra Fort, Agra' },
];

const SHARE_TEXT = 'Delhi and the Taj Mahal in one weekend, every ticket included. ₹7,499 per person. Join me?';
const ShareButton = ({ testId }) => {
  const href = `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${window.location.origin}/weekend-tour`)}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="wt-btn-outline" data-testid={testId}>
      <Share2 size={16} />Share with friends
    </a>
  );
};

const days = [
  {
    key: 'saturday', label: 'Saturday', title: 'Delhi Heritage', icon: Landmark,
    body: ["Red Fort (including the museum), Qutub Minar, Humayun's Tomb, then Akshardham Temple and its evening water show.",
      'Early-morning departure from Aerocity; the exact time is confirmed after booking. The day ends at Akshardham. Make your own way home by Metro or cab.'],
  },
  {
    key: 'sunday', label: 'Sunday', title: 'Delhi to Agra', icon: Sun,
    body: ['7:00 AM departure from Aerocity by Volvo. Brunch break in Agra (at your own cost), the Taj Mahal, then Agra Fort. Back in Delhi at approximately 9-10 PM.'],
  },
];

const included = ['Volvo coach on both days',
  "Entry tickets for every monument: Red Fort and its museum, Qutub Minar, Humayun's Tomb, Akshardham and the water show, Taj Mahal, Agra Fort",
  'Licensed guides'];
const notIncluded = ['Meals, including the brunch in Agra', 'Accommodation (there is no overnight stay)',
  "Your travel home after Saturday's tour and to the Aerocity pickup", 'Personal expenses'];

const steps = [
  { icon: MessageCircle, text: 'Message us on WhatsApp. We confirm your dates and seats.' },
  { icon: Users, text: 'Share the names of everyone travelling.' },
  { icon: CreditCard, text: 'Pay ₹7,499 per person by UPI, in full, before Friday 3 PM.' },
  { icon: ShieldCheck, text: 'We verify your payment and confirm your seat. The exact Aerocity meeting point and trip details are sent after confirmation.' },
];

const refunds = ['If fewer than 20 guests are confirmed by Friday 3 PM, the departure is cancelled. You choose a full refund or a free move to another date.',
  'Cancel before Friday 3 PM: full refund.',
  "After Friday 3 PM, or if you don't show up: no refund, unless your seat is resold.",
  'If a monument closure, weather or other circumstances beyond our control affect a day, we reschedule or refund that day.',
  'Refunds go to the paying UPI account within 5 working days.'];

const faqs = [
  ['Who is this tour for?', 'Indian nationals: weekenders, families and friend groups. Foreign guests can message us on WhatsApp for other options.'],
  ['What does ₹7,499 cover?', 'Transport, every monument ticket and licensed guides. Only meals and personal spending are extra.'],
  ['Do children pay?', 'The price is ₹7,499 per person for every guest, whatever the age.'],
  ['Is food included?', 'No. We stop for brunch in Agra at your own cost.'],
  ['Where do we meet?', 'At Aerocity. The exact meeting point is shared after your booking is confirmed.'],
  ['How do I get home on Saturday?', 'The Saturday tour ends at Akshardham. Use the Metro or book your own cab.'],
  ['What should I wear?', 'Comfortable shoes and decent, full-sleeve clothing suited to Delhi winters. Akshardham has strict security checks; we share what to carry when you confirm.'],
];

const WaButton = ({ label, testId, className = '' }) => (
  <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className={`wt-btn ${className}`} data-testid={testId}>
    {label}<ArrowRight size={18} />
  </a>
);

const Label = ({ children }) => <p className="wt-label">{children}</p>;

const Hero = ({ hasGroupPrices }) => (
  <section className="wt-section wt-hero" data-testid="wt-hero">
    <h1 className="wt-h1">Delhi and the Taj Mahal in one weekend. Every ticket included.</h1>
    <p className="wt-lead">A fixed-departure, two-day group tour by Volvo with licensed guides. Departs every Saturday and Sunday through March.</p>
    <p className="wt-price" data-testid="wt-hero-price">₹7,499 <span>per person</span></p>
    <p className="wt-muted" data-testid="wt-hero-price-note">
      {hasGroupPrices ? 'Same price for every guest, whatever the age. Lower prices for bigger groups.' : 'Same price for every guest, whatever the age or group size.'}
    </p>
    <div className="wt-chips">
      {['6 monuments', 'All tickets included', "Full refund if the group doesn't fill"].map((c) => <span key={c} className="wt-chip">{c}</span>)}
    </div>
    <div className="wt-actions">
      <WaButton label="Check seats on WhatsApp" testId="wt-hero-whatsapp-btn" />
      <ShareButton testId="wt-hero-share-btn" />
    </div>
  </section>
);

const Weekend = () => (
  <section className="wt-section" data-testid="wt-your-weekend">
    <h2 className="wt-h2">Your weekend</h2>
    <div className="wt-days">
      {days.map(({ key, label, title, icon: Icon, body }) => (
        <article key={key} className="wt-card" data-testid={`wt-day-${key}`}>
          <div className="wt-card-head">
            <div><Label>{label}</Label><h3 className="wt-h3">{title}</h3></div>
            <span className="wt-icon"><Icon size={18} /></span>
          </div>
          <div className="wt-card-body">{body.map((p) => <p key={p}>{p}</p>)}</div>
        </article>
      ))}
    </div>
  </section>
);

const ListBlock = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <Label>{title}</Label>
    <ul className="wt-list">{items.map((i) => <li key={i}>{i}</li>)}</ul>
  </div>
);

const Inclusions = () => (
  <section className="wt-section wt-grid2" data-testid="wt-inclusions">
    <ListBlock title="What's included" items={included} testId="wt-included" />
    <ListBlock title="What's not included" items={notIncluded} testId="wt-not-included" />
  </section>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardLink = (d, seats) => {
  const text = `Hi! I want to book the Delhi + Agra Weekend departing Saturday ${d.saturday_label.replace('Saturday ', '')}. Seats needed: ${seats}`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
};

const PRICE = 7499;

const priceFor = (seats, tiers) =>
  tiers.reduce((p, t) => (seats >= t.min_seats && t.price_per_person < p ? t.price_per_person : p), PRICE);

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const GroupHint = ({ seats, tiers, testId }) => {
  const each = priceFor(seats, tiers);
  if (each < PRICE) return <p className="wt-group-hint" data-testid={testId}>Group price: {inr(each)} per person</p>;
  const nextTier = tiers.find((t) => t.min_seats > seats);
  return nextTier ? <p className="wt-muted wt-group-next" data-testid={testId}>{nextTier.min_seats}+ seats: {inr(nextTier.price_per_person)} per person</p> : null;
};

const SeatPicker = ({ d, seats, setSeats, tiers }) => (
  <div className="wt-seats">
    <label>
      <span>Seats needed</span>
      <select value={seats} onChange={(e) => setSeats(Number(e.target.value))} data-testid={`wt-seat-select-${d.saturday}`}>
        {Array.from({ length: d.seats_left }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </label>
    <span className="wt-total" data-testid={`wt-seat-total-${d.saturday}`}>
      {seats} {seats === 1 ? 'seat' : 'seats'} · {inr(seats * priceFor(seats, tiers))}
    </span>
  </div>
);

const timeLeft = (ms) => {
  const m = Math.floor(ms / 60000);
  const days = Math.floor(m / 1440), hours = Math.floor((m % 1440) / 60), mins = m % 60;
  const part = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (days > 0) return `${part(days, 'day')} ${part(hours, 'hour')}`;
  if (hours > 0) return `${part(hours, 'hour')} ${part(mins, 'minute')}`;
  return part(Math.max(mins, 1), 'minute');
};

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  return now;
};

const DepartureCard = ({ d, now, isNext, tiers }) => {
  const [seats, setSeats] = useState(1);
  const msLeft = new Date(d.closes_at).getTime() - now;
  const closed = d.closed || msLeft <= 0;
  return (
  <article className="wt-card wt-dep" data-testid={`wt-departure-${d.saturday}`}>
    <div className="wt-card-head">
      <div>
        <h3 className="wt-h3">{d.saturday_label}</h3>
        <p className="wt-muted">{d.sunday_label}</p>
      </div>
    </div>
    <div className="wt-card-body">
      {closed ? <p className="wt-dep-status" data-testid="wt-departure-closed">Booking closed</p> : (
        <>
          <p className="wt-dep-seats" data-testid="wt-departure-seats">{d.seats_left} seats left</p>
          <p className="wt-dep-status" data-testid="wt-departure-status">
            {d.confirmed ? 'Departure confirmed' : `${d.guests_needed} more guests needed to confirm`}
          </p>
          {isNext
            ? <p className="wt-countdown" data-testid="wt-departure-countdown">Booking closes in {timeLeft(msLeft)}</p>
            : <p className="wt-muted">Booking closes Friday 3:00 PM</p>}
          <SeatPicker d={d} seats={seats} setSeats={setSeats} tiers={tiers} />
          <GroupHint seats={seats} tiers={tiers} testId={`wt-group-hint-${d.saturday}`} />
          <a href={cardLink(d, seats)} target="_blank" rel="noopener noreferrer" className="wt-btn wt-dep-btn" data-testid="wt-departure-book-btn">
            Book this departure<ArrowRight size={18} />
          </a>
        </>
      )}
    </div>
  </article>
  );
};

const Departures = ({ tiers }) => {
  const [deps, setDeps] = useState([]);
  const now = useNow();
  const next = deps.find((d) => !d.closed && new Date(d.closes_at).getTime() > now);
  useEffect(() => {
    axios.get(`${API}/departures`).then((r) => setDeps(r.data)).catch(() => setDeps([]));
  }, []);
  return (
    <section className="wt-section" data-testid="wt-departures">
      <h2 className="wt-h2">Next departures</h2>
      {deps.length > 0 ? (
        <div className="wt-days wt-departures-grid" data-testid="wt-departure-cards">{deps.map((d) => <DepartureCard key={d.saturday} d={d} now={now} isNext={d === next} tiers={tiers} />)}</div>
      ) : (
        <>
          <p className="wt-lead" data-testid="wt-departures-fallback">Dates and seats are shared on WhatsApp</p>
          <WaButton label="Check seats on WhatsApp" testId="wt-departures-whatsapp-btn" />
        </>
      )}
      <p className="wt-muted wt-mt">Each departure runs with a minimum of 20 guests and a maximum of 38 seats.</p>
    </section>
  );
};

const Booking = () => (
  <section className="wt-section" data-testid="wt-how-booking-works">
    <h2 className="wt-h2">How booking works</h2>
    <ol className="wt-steps">
      {steps.map(({ icon: Icon, text }, i) => (
        <li key={i} className="wt-step" data-testid={`wt-step-${i + 1}`}>
          <span className="wt-step-num">{String(i + 1).padStart(2, '0')}</span>
          <span className="wt-icon"><Icon size={18} /></span>
          <p>{text}</p>
        </li>
      ))}
    </ol>
  </section>
);

const Gallery = () => galleryImages.length === 0 ? null : (
  <section className="wt-section" data-testid="wt-gallery">
    <h2 className="wt-h2">Along the way</h2>
    <div className="wt-gallery">{galleryImages.map(({ src, alt }) => <img key={src} src={src} alt={alt} loading="lazy" />)}</div>
  </section>
);

const Team = () => (
  <section className="wt-section" data-testid="wt-team">
    <h2 className="wt-h2">Who you'll travel with</h2>
    <p className="wt-lead">Joshua Massey, Tourist Facilitator, and Ajay, Guide.</p>
    <p className="wt-body">Licensed under the Ministry of Tourism, Government of India (Incredible India). Delhi Guided Tours, New Delhi.</p>
  </section>
);

const Refunds = () => (
  <section className="wt-section" data-testid="wt-refunds">
    <h2 className="wt-h2">Cancellation and refunds</h2>
    <ul className="wt-list">{refunds.map((r) => <li key={r}>{r}</li>)}</ul>
    <Link to="/refund-policy" className="wt-link" data-testid="wt-refund-policy-link">Read the full refund policy <ArrowRight size={16} /></Link>
  </section>
);

const Faq = ({ tiers }) => {
  const items = tiers.length ? [...faqs, ['Are there group prices?', `Yes. ${tiers.map((t) => `${t.min_seats}+ seats: ₹${t.price_per_person.toLocaleString('en-IN')} per person.`).join(' ')}`]] : faqs;
  return (
  <section className="wt-section" data-testid="wt-faq">
    <h2 className="wt-h2">FAQ</h2>
    <Accordion type="single" collapsible className="wt-faq">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="wt-faq-item">
          <AccordionTrigger className="wt-faq-q" data-testid={`wt-faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="wt-faq-a" data-testid={`wt-faq-a-${i}`}>{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
  );
};

const FinalCta = () => (
  <section className="wt-section wt-final" data-testid="wt-final-cta">
    <h2 className="wt-h1">Ready for your weekend?</h2>
    <div className="wt-actions">
      <WaButton label="Book on WhatsApp" testId="wt-final-whatsapp-btn" />
      <ShareButton testId="wt-final-share-btn" />
    </div>
  </section>
);

const StickyBar = () => (
  <div className="wt-sticky" data-testid="wt-sticky-bar">
    <WaButton label="Book on WhatsApp - ₹7,499" testId="wt-sticky-whatsapp-btn" className="wt-btn-full" />
  </div>
);

export default function WeekendTour() {
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/group-prices`).then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);
  useEffect(() => {
    document.body.classList.add('wt-has-sticky');
    return () => document.body.classList.remove('wt-has-sticky');
  }, []);
  return (
    <div className="wt-page" data-testid="weekend-tour-page">
      <div className="wt-container">
        <Hero hasGroupPrices={tiers.length > 0} /><Weekend /><Inclusions /><Departures tiers={tiers} /><Booking /><Gallery /><Team /><Refunds /><Faq tiers={tiers} /><FinalCta />
      </div>
      <StickyBar />
    </div>
  );
}
